import {
    Fragment,
    type FunctionComponent,
    type ReactNode,
    useState,
    useEffect,
    useCallback,
} from 'react';
import { useRelayEnvironment, useLazyLoadQuery, graphql } from 'react-relay';
import classnames from 'classnames';

import ArrowDown from 'dibs-icons/exports/legacy/ArrowDown.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import RecaptchaContainer, {
    isValidErrorCode,
    mfaActionTypes,
    type AllowedMFAErrorCodes,
    type MFAActionType,
} from 'dibs-recaptcha/exports/RecaptchaContainer.ts';
import serverVars from 'server-vars';

import LoginFormWrapper from '../LoginFormWrapper.tsx';
import LoginMessage, { type LoginMessageType } from '../LoginMessage.tsx';
import LoginFormGroup from '../inputs/LoginFormGroup.tsx';
import LoginEmailInput from '../inputs/LoginEmailInput.tsx';
import LoginPasswordInput from '../inputs/LoginPasswordInput.tsx';
import LoginSubmit from '../inputs/LoginSubmit.tsx';
import LoginResetPasswordLink from '../inputs/LoginResetPasswordLink.tsx';
import InternalLoginDealerLink from './InternalLoginDealerLink.tsx';
import { useLoginFormState } from '../helpers/useLoginFormState.ts';
import {
    internalLoginMutation,
    type InternalLoginInput,
} from './mutations/internalLoginMutation.ts';
import LoginServerErrorMessage from '../LoginServerErrorMessage.tsx';
import LoginInvalidErrorMessage from '../LoginInvalidErrorMessage.tsx';
import LoginSuspiciousErrorMessage from '../LoginSuspiciousErrorMessage.tsx';
import { getGoToLocation } from '../helpers/loginGoTo.ts';
import { getRelayState } from '../helpers/getRelayState.ts';
import { useLoginMutation } from '../helpers/useLoginMutation.tsx';
import { type DibsRelayNetworkError } from 'dibs-relay-network-layer';

import OktaLoginButton from '../okta/OktaLoginButton.tsx';
import OktaWidgetLoader from '../okta/OktaWidgetLoader.tsx';

import styles from './styles/InternalLoginForm.scss';

import { type internalLoginMutation as internalLoginMutationType } from './mutations/__generated__/internalLoginMutation.graphql.ts';
import { type InternalLoginFormQuery } from './__generated__/InternalLoginFormQuery.graphql.ts';

function getErrorMessage(errorCode: boolean | DibsRelayNetworkError): string | boolean | undefined {
    if (typeof errorCode !== 'boolean') {
        return errorCode?.source?.errors?.[0]?.message;
    }
    return errorCode;
}

type Props = {
    successMessage?: ReactNode;
};

const internalUserEmail = new RegExp('^[A-Za-z0-9._%+-]+@1stdibs.com$');

function isInternalUser(email: string): boolean {
    return internalUserEmail.test(email);
}

function isOktaLogin(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    return (searchParams.get('login') || '').toLowerCase() === 'okta';
}
const OKTA_SAML_IDP_INTERNAL = serverVars.get('OKTA_SAML_IDP_INTERNAL');

const InternalUseOktaMessage: ReactNode = (
    <div>
        1stDibs employees must log in using Okta.{' '}
        <Link
            href="https://internalsupport.1stdibs.com/hc/en-us/articles/360050799572"
            target="_blank"
            className={styles.internalErrorLink}
        >
            Need more info?
        </Link>
    </div>
);

const InternalUseEmailMessage: ReactNode = (
    <div>Non qa, stage, and prod servers can use the email/password login functionality.</div>
);

const InternalLoginForm: FunctionComponent<Props> = ({ successMessage }) => {
    const [showLoginForm, setShowLoginForm] = useState(false);
    const [showOktaWidget, setShowOktaWidget] = useState(() => isOktaLogin());
    const [oktaOpened, setOktaOpened] = useState(() => isOktaLogin());
    const [message, setMessage] = useState<ReactNode>('');
    const [messageType, setMessageType] = useState<LoginMessageType>('success');
    const { errors, isValid, values, touched, handleChange, handleFieldBlur } = useLoginFormState({
        formType: 'login',
        isInternal: true,
    });
    const environment = useRelayEnvironment();
    const { email } = values;
    const isInternalUserEmail = isInternalUser(email);

    const { viewer } = useLazyLoadQuery<InternalLoginFormQuery>(
        graphql`
            query InternalLoginFormQuery {
                viewer {
                    internalLoginOktaSaml: featureFlag(feature: "internalLoginOktaSaml")
                    useOktaLogin
                }
            }
        `,
        {}
    );

    const { internalLoginOktaSaml } = viewer || {};
    const useOktaLogin = internalLoginOktaSaml || viewer.useOktaLogin;

    // MFA
    const [displayRecaptcha, setDisplayRecaptcha] = useState(false);
    const [mfaVerificationCode, setMFAVerificationCode] = useState<string | null>(null);
    const [mfaErrorCode, setMFAErrorCode] = useState<AllowedMFAErrorCodes | null>(null);
    const [mfaActionType, setMFAActionType] = useState<MFAActionType>(mfaActionTypes.displayed);
    // should only auto close when attempts exceeded
    const [mfaShouldAutoClose, setMFAShouldAutoClose] = useState(false);

    const resetMFADefaults = (): void => {
        // allows for the RecaptchaContainer
        // component to reset to default data
        // when toggling views
        setDisplayRecaptcha(false);
        setMFAVerificationCode(null);
        setMFAErrorCode(null);
        setMFAShouldAutoClose(false);
    };
    // end MFA

    const { errorCode, hasSubmitted, isSaving, handleSubmit } = useLoginMutation<
        internalLoginMutationType,
        InternalLoginInput,
        boolean
    >({
        isValid,
        mutationFn: internalLoginMutation,
        variables: {
            email,
            password: values.password,
            mfaVerificationCode,
            cookieDomain: serverVars.get('cookieDomain'),
        },
        getErrorCode: () => false, // no errorcodes from this mutation
        environment,
        handleFieldBlur,
        onSuccess: () => {
            setMFAActionType(mfaActionTypes.displayed);
            window.location.assign(getGoToLocation('/internal/'));
        },
    });

    const handleMFAError = useCallback(
        (error: boolean | DibsRelayNetworkError) => {
            const msg = getErrorMessage(error);
            if (isValidErrorCode(msg)) {
                setMFAErrorCode(msg as AllowedMFAErrorCodes);
                setDisplayRecaptcha(true);
                if (msg === 'MFA_CODE_ATTEMPTS_EXCEEDED') {
                    setMFAShouldAutoClose(true);
                }
            }
        },
        [setMFAErrorCode, setDisplayRecaptcha, setMFAShouldAutoClose]
    );

    useEffect(() => {
        if (errorCode) {
            setMessageType('error');

            const errorMsg = getErrorMessage(errorCode);
            if (typeof errorMsg === 'string' && errorMsg.startsWith('MFA_')) {
                handleMFAError(errorCode);
            } else if (errorMsg === 'SUSPICIOUS_ACTIVITY') {
                setMessage(<LoginSuspiciousErrorMessage />);
            } else if (errorMsg === 'INVALID_LOGIN') {
                setMessage(<LoginInvalidErrorMessage />);
            } else {
                setMessage(<LoginServerErrorMessage />);
            }
        } else if (successMessage && !hasSubmitted) {
            setMessage(successMessage);
        } else if (isInternalUserEmail) {
            if (useOktaLogin) {
                setMessageType('error');
                setMessage(InternalUseOktaMessage);
            } else {
                setMessageType('success');
                setMessage(InternalUseEmailMessage);
            }
        }
    }, [
        errorCode,
        hasSubmitted,
        isInternalUserEmail,
        useOktaLogin,
        successMessage,
        handleMFAError,
    ]);

    useEffect(() => {
        window.onpopstate = () => {
            setShowOktaWidget(isOktaLogin());
        };
    }, []);

    const oktaLink = OKTA_SAML_IDP_INTERNAL ? new URL(OKTA_SAML_IDP_INTERNAL) : null;
    oktaLink?.searchParams.set('RelayState', getRelayState());

    const socialLoginButton = useOktaLogin ? (
        <OktaLoginButton
            key="okta-login-button"
            {...(internalLoginOktaSaml
                ? { href: oktaLink?.href || undefined }
                : {
                      onClick: () => {
                          setShowOktaWidget(true);
                          setOktaOpened(true);
                      },
                  })}
        />
    ) : null;

    return (
        <Fragment>
            {useOktaLogin && (
                <div className={classnames({ [styles.hide]: !showOktaWidget })}>
                    <OktaWidgetLoader isVisible={showOktaWidget} oktaOpened={oktaOpened} />
                </div>
            )}
            <div className={classnames({ [styles.hide]: useOktaLogin && showOktaWidget })}>
                {message && <LoginMessage messageType={messageType} message={message} />}
                <LoginFormWrapper
                    heading="Administration Login"
                    socialLoginButtons={socialLoginButton}
                    handleSubmit={handleSubmit}
                    hasMessage={!!message}
                >
                    <div
                        className={classnames(styles.loginLinksWrapper, {
                            [styles.dealerLinkOnly]: !useOktaLogin,
                        })}
                    >
                        {useOktaLogin && (
                            <Link
                                onClick={() => setShowLoginForm(!showLoginForm)}
                                dataTn="email-password-login"
                                className={styles.passwordLoginCTA}
                            >
                                Login with Password
                                <ArrowDown
                                    className={classnames(styles.arrowDown, {
                                        [styles.arrowUp]: showLoginForm,
                                    })}
                                />
                            </Link>
                        )}
                        <InternalLoginDealerLink />
                    </div>

                    {(showLoginForm || !useOktaLogin) && (
                        <>
                            {displayRecaptcha ? (
                                <RecaptchaContainer
                                    email={email}
                                    resetMFAData={resetMFADefaults}
                                    mfaVerificationCode={mfaVerificationCode}
                                    setMFAVerificationCode={setMFAVerificationCode}
                                    errorCode={mfaErrorCode} // : MFAErrorCodes | null;
                                    mfaActionType={mfaActionType} //: typeof mfaActionTypes[keyof typeof mfaActionTypes];
                                    isMobile={false}
                                    trackingCategory="login"
                                    trackingLabel="external resource login form" //string
                                    shouldAutoClose={mfaShouldAutoClose}
                                />
                            ) : (
                                <>
                                    <LoginFormGroup>
                                        <LoginEmailInput
                                            value={email}
                                            touched={touched.email}
                                            error={errors.email}
                                            onBlur={handleFieldBlur}
                                            onChange={handleChange}
                                        />
                                    </LoginFormGroup>
                                    <LoginFormGroup>
                                        <LoginPasswordInput
                                            value={values.password}
                                            touched={touched.password}
                                            error={errors.password}
                                            onBlur={handleFieldBlur}
                                            onChange={handleChange}
                                        />
                                    </LoginFormGroup>
                                    <LoginResetPasswordLink href="/login/reset_pw/internal/new" />
                                </>
                            )}
                            <LoginSubmit isSaving={isSaving} />
                        </>
                    )}
                </LoginFormWrapper>
            </div>
        </Fragment>
    );
};

export default InternalLoginForm;
